<template>
  <two-column-layout>
    <card-container>
      <h2>{{ $t(i18n.additional_coverages_step.title) }}</h2>
      <p>{{ $t(i18n.additional_coverages_step.description) }}</p>

      <template v-for="coverage in getModelCoverages('ADDITIONAL')">
        <radio-group-fragment
          :key="coverage.code"
          :label="$t(i18n.radio_group.coverage[coverage.code].label)"
          :group-name="`group coverage ${coverage.code}`"
          :options="radioGroupYesNoOptions"
          show-info
          v-model="form.coverage[coverage.code]"
          @info-clicked="onInfoClicked(`REGISTER_SINGLE_VEHICLE_INFO_${coverage.code}`)"
        >
          <template #result>
            <field-error-messages :validation-state="$v.form.coverage[coverage.code]"/>
          </template>
        </radio-group-fragment>
      </template>

      <template v-for="coverage in getModelCoverages('EXTRA')">
        <text-fragment
          :key="coverage.code"
          :label="$t(i18n.coverage[coverage.code].label)"
          show-info
          @info-clicked="onInfoClicked(`REGISTER_SINGLE_VEHICLE_INFO_${coverage.code}`)"
        >
          <span>{{ $t(i18n.coverage[coverage.code].description) }}</span>
        </text-fragment>
      </template>
    </card-container>

    <button-group direction="right" top-spacing="l">
      <button-fragment icon-before="back"
                       as-link
                       @click="previousStep()">{{$t(i18n.button.previous)}}
      </button-fragment>
      <button-fragment @click="nextStep()">{{$t(i18n.button.next)}}</button-fragment>
    </button-group>

    <template #right>
      <card-container>
        <receipt-group-fragment :label="$t(i18n.receipt.title)">
          <template #description>{{$t(i18n.receipt.description)}}</template>
          <receipt-item-fragment :label="$t(i18n.receipt.vehicle)">
            {{vehicle.licensePlate}}<br>
            {{vehicle.type}}<br>
            {{vehicle.info}}
          </receipt-item-fragment>
          <receipt-item-fragment :label="$t(i18n.receipt.usage)">{{$t(i18n.radio_group.vehicle_utilization[vehicleUtilization])}}</receipt-item-fragment>
          <receipt-item-fragment :label="$t(i18n.receipt.coverage.WA)">{{hasSelectedCoverage("WA")}}</receipt-item-fragment>
          <receipt-item-fragment :label="$t(i18n.receipt.coverage.casco.label)">{{$t(i18n.receipt.coverage.casco[coverage.CASCO])}}</receipt-item-fragment>
        </receipt-group-fragment>
      </card-container>
      <media-item-card blank item-tag="REGISTER_SINGLE_VEHICLE_INFO_RECEIPT" />
    </template>
  </two-column-layout>
</template>

<script>
  import {Utils} from "@bvm/core";
  import {createNamespacedHelpers} from "vuex";
  import {required} from "vuelidate/lib/validators";
  import {
    ButtonFragment,
    ButtonGroup,
    CardContainer,
    RadioGroupFragment,
    ReceiptGroupFragment,
    ReceiptItemFragment,
    TextFragment,
    TwoColumnLayout
  } from "@bvm/forms";
  import MediaItemModal from "../../shared/media-item-modal";
  import MediaItemCard from "../components/media-item-card";
  import FieldErrorMessages from "../../shared/field-error-messages";

  const {mapState, mapMutations} = createNamespacedHelpers("registerSingleVehicle");

const i18nBuilder = new Utils.I18n.I18nBuilder("register_single_vehicle_form")
  .add("additional_coverages_step", b => b.add("title").add("description"))
  .add("button", b => b.add("previous").add("next"))
  .add("receipt", b => b
    .add("title").add("description")
    .add("vehicle")
    .add("usage")
    .add("coverage", c => c
      .add("WA")
      .add("casco", d => d.add("label").add("limited").add("full").add("none"))
    )
  )
  .add("coverage", b => b
    .add("INVT", c => c.add("label").add("description"))
  )
  .add("radio_group", b => b
    .add("generic", c => c.add("yes").add("no"))
    .add("vehicle_utilization", b => b.add("label").add("leasing").add("teaching").add("renting").add("business"))
    .add("coverage", c => c
      .add("OVI", d => d.add("label"))
      .add("SVI", d => d.add("label"))
      .add("VRB", d => d.add("label"))
      .add("PH", d => d.add("label"))
      .add("VV", d => d.add("label"))
    )
  );

export const messageKeys = i18nBuilder.getKeys();

export default {
  name: "additional-coverages-step",
  components: {
    TwoColumnLayout,
    CardContainer,
    ButtonGroup,
    ButtonFragment,
    RadioGroupFragment,
    TextFragment,
    ReceiptGroupFragment,
    ReceiptItemFragment,
    MediaItemCard,
    FieldErrorMessages
  },
  props: {},
  data() {
    return {
      i18n: i18nBuilder.getData(),
      form: {
        coverage: {}
      }
    };
  },
  computed: {
    ...mapState(["vehicle", "vehicleUtilization", "coverage", "productModelCoverages"]),
    radioGroupYesNoOptions() {
      return {
        y: this.$t(this.i18n.radio_group.generic.yes),
        n: this.$t(this.i18n.radio_group.generic.no)
      };
    }
  },
  created() {
    // Invoke loadstep on initial creation because refs used in parent are not yet available
    this.loadStep();
  },
  methods: {
    ...mapMutations(["setCoverages"]),
    loadStep() {
      // Set possible coverage values from store, set default value of additional coverages to 'n'
      // Make dynamically added coverages reactive so that processes (like vuelidate validations) can subscribe to them
      this.getModelCoverages("ADDITIONAL")
          .forEach(coverage => this.$set(this.form.coverage, coverage.code, this.coverage[coverage.code]));

      this.getModelCoverages("EXTRA")
          .forEach(coverage => this.$set(this.form.coverage, coverage.code, this.coverage[coverage.code]));
    },
    hasSelectedCoverage(coverageToCheck) {
      return this.coverage[coverageToCheck] === "y"
        ? this.$t(this.i18n.radio_group.generic.yes)
        : this.$t(this.i18n.radio_group.generic.no);
    },
    getModelCoverages() {
      let types = Array.from(arguments);
      return this.productModelCoverages
        .filter(coverage => types.includes(coverage.type))
        .sort((a, b) => this.getCoverageSortingIndex(a.code) - this.getCoverageSortingIndex(b.code));
    },
    onInfoClicked(mediaItemTag) {
      this.$bvmModal.show(MediaItemModal, {
          itemTag: mediaItemTag
      }, {
          clickToClose: true
      });
    },
    previousStep(value) {
      this.$emit("previousStep", value);
    },
    nextStep() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.updateToState();
        this.$emit("nextStep");
      }
    },
    updateToState() {
      // Set coverages with type EXTRA default to selected
      this.getModelCoverages("EXTRA")
          .forEach(coverage => this.form.coverage[coverage.code] = 'y');

      this.setCoverages(this.form.coverage);
    },
    getCoverageSortingIndex(coverage) {
      switch (coverage) {
        case 'SVI':
          return 1;
        case 'OVI':
          return 2;
        case 'VRB':
          return 3;
        case 'VV':
          return 4;
        default:
          return 5;
      }
    }
  },
  validations() {
    let validationMapping = this.getModelCoverages("ADDITIONAL").reduce(function(map, coverage) {
      map[coverage.code] = {"option_required": required};
        return map;
    }, {});

    return {
      form: {
        coverage: validationMapping
      }
    };
  }
};
</script>

<style lang="scss"></style>
